import {
 useState, useEffect, useRef
} from "react";
import {
 ArrowTopRightOnSquareIcon
} from "@heroicons/react/24/outline";
import {
 getUserFacingApiErrorMessage,
 slackService
} from "../services/api";
import type {
 SlackOAuthInitResponse
} from "../services/api";
import {
 OnboardingEvents,
 trackEvent
} from '../analytics/posthog';
import {
  OAuthWaitingState
} from "./pairing/OAuthWaitingState";
import {
  PairingModalShell
} from "./pairing/PairingModalShell";
import {
  clearOAuthRedirectFlow,
  oauthCallbackChannelName,
  markOAuthRedirectFlow,
  type OAuthCallbackResult
} from "./pairing/oauthCallbackChannel";
import {
  isMobileBrowser
} from "../utils/inAppBrowser";

const POPUP_STATUS_GRACE_MS = 15000;
const POPUP_STATUS_POLL_MS = 1000;
const POPUP_LATE_STATUS_WINDOW_MS = 60000;
const POPUP_LATE_STATUS_POLL_MS = 3000;
const REDIRECT_WATCHDOG_MS = 3000;

type OAuthAttemptState = "idle" | "pending" | "abandoned" | "settled";

/**
 * The two Slack OAuth flows share this modal: the workspace (bot) install and
 * the owner's own read-only connection (GET-14181). They differ only in which
 * backend endpoints they hit, the callback page's message types, and copy —
 * the popup/redirect/polling machinery is identical, so it is parameterised
 * rather than copied.
 */
export interface SlackOAuthFlow {
  /** Key for the redirect-flow markers and BroadcastChannel name. */
  providerName: string;
  successType: string;
  errorType: string;
  initiateOAuth: () => Promise<SlackOAuthInitResponse>;
  isConnected: () => Promise<boolean>;
  title: string;
  description: string;
  buttonLabel: string;
}

export const SLACK_WORKSPACE_FLOW: SlackOAuthFlow = {
  providerName: "Slack",
  successType: "SLACK_OAUTH_SUCCESS",
  errorType: "SLACK_OAUTH_ERROR",
  initiateOAuth: () => slackService.initiateOAuth(),
  isConnected: async () => (await slackService.getStatus()).connected,
  title: "Connect Slack Workspace",
  description:
    "Connect your Slack workspace to chat with your assistant and get updates right in Slack — one click away in Slack's top bar.",
  buttonLabel: "Connect to Slack",
};

interface SlackPairingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAttemptStarted?: () => void;
  onSuccess: () => void;
  flow?: SlackOAuthFlow;
}

export function SlackPairingModal({
  isOpen,
  onClose,
  onAttemptStarted,
  onSuccess,
  flow = SLACK_WORKSPACE_FLOW,
}: SlackPairingModalProps) {
  const [oauthData, setOauthData] = useState<SlackOAuthInitResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollingForCallback, setPollingForCallback] = useState(false);
  const modalOpenTracked = useRef(false);
  const popupPollTimerRef = useRef<number | null>(null);
  const popupTimeoutRef = useRef<number | null>(null);
  const statusPollTimerRef = useRef<number | null>(null);
  const redirectTimerRef = useRef<number | null>(null);
  const cancelRedirectListenersRef = useRef<(() => void) | null>(null);
  const activeAttemptRef = useRef(0);
  const attemptNumberRef = useRef(0);
  const attemptStateRef = useRef<OAuthAttemptState>("idle");
  const activeOAuthStateRef = useRef<string | null>(null);

  const clearPopupWatch = () => {
    if (popupPollTimerRef.current !== null) {
      clearInterval(popupPollTimerRef.current);
      popupPollTimerRef.current = null;
    }
    if (popupTimeoutRef.current !== null) {
      clearTimeout(popupTimeoutRef.current);
      popupTimeoutRef.current = null;
    }
  };

  const clearRedirectWatch = () => {
    if (redirectTimerRef.current !== null) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    cancelRedirectListenersRef.current?.();
    cancelRedirectListenersRef.current = null;
  };

  const stopPolling = () => {
    clearPopupWatch();
    clearRedirectWatch();
    if (statusPollTimerRef.current !== null) {
      clearTimeout(statusPollTimerRef.current);
      statusPollTimerRef.current = null;
    }
  };

  const handleSuccess = (transport: "post_message" | "broadcast_channel" | "status_poll") => {
    if (attemptStateRef.current === "idle" || attemptStateRef.current === "settled") return;
    attemptStateRef.current = "settled";
    stopPolling();
    if (transport !== "status_poll") {
      trackEvent(OnboardingEvents.SLACK_OAUTH_CALLBACK_RECEIVED, {
        success: true,
        transport,
      });
    }
    trackEvent(OnboardingEvents.SLACK_CONNECTED, { transport });
    setOauthData(null);
    setError(null);
    setPollingForCallback(false);
    onClose();
    onSuccess();
  };

  const handleOAuthError = (errorMessage: string, transport: "post_message" | "broadcast_channel") => {
    if (attemptStateRef.current === "idle" || attemptStateRef.current === "settled") return;
    attemptStateRef.current = "settled";
    stopPolling();
    trackEvent(OnboardingEvents.SLACK_OAUTH_CALLBACK_RECEIVED, {
      success: false,
      error: errorMessage,
      transport,
    });
    trackEvent(OnboardingEvents.SLACK_CONNECTION_FAILED, { error: errorMessage });
    setPollingForCallback(false);
    setError(errorMessage);
  };

  const handleOAuthResult = (
    data: unknown,
    transport: "post_message" | "broadcast_channel",
  ): void => {
    if (!data || typeof data !== "object") return;
    const result = data as OAuthCallbackResult;
    if (typeof result.type !== "string") return;
    // A result without state cannot be attributed safely to this attempt.
    // Slack normally echoes state on denial; polling supplies the safe fallback.
    if (typeof result.state !== "string" || result.state !== activeOAuthStateRef.current) return;

    if (result.type === flow.successType) {
      handleSuccess(transport);
    } else if (result.type === flow.errorType) {
      handleOAuthError(
        typeof result.error === "string" ? result.error : "OAuth authentication failed",
        transport,
      );
    }
  };

  useEffect(() => () => {
    activeAttemptRef.current += 1;
    attemptStateRef.current = "settled";
    stopPolling();
  }, []);

  useEffect(() => {
    if (isOpen && !modalOpenTracked.current) {
      trackEvent(OnboardingEvents.SLACK_MODAL_OPENED);
      modalOpenTracked.current = true;
    }
    if (!isOpen) {
      modalOpenTracked.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !oauthData) {
      initiateSlackOAuth();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleCallback = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      handleOAuthResult(event.data, "post_message");
    };

    window.addEventListener("message", handleCallback);
    return () => window.removeEventListener("message", handleCallback);
  }, []);

  useEffect(() => {
    if (!isOpen || !oauthData?.state || typeof BroadcastChannel === "undefined") {
      return;
    }

    const channel = new BroadcastChannel(oauthCallbackChannelName(flow.providerName, oauthData.state));
    channel.addEventListener("message", (event) => {
      handleOAuthResult(event.data, "broadcast_channel");
    });
    return () => channel.close();
  }, [isOpen, oauthData?.state]);

  const initiateSlackOAuth = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await flow.initiateOAuth();
      activeOAuthStateRef.current = data.state;
      setOauthData(data);
    } catch (err) {
      activeOAuthStateRef.current = null;
      const errorMessage = getUserFacingApiErrorMessage(
        err,
        "Couldn't start connecting Slack just now. Give it another try.",
      );
      trackEvent(OnboardingEvents.SLACK_CONNECTION_FAILED, { error: errorMessage });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openSlackOAuth = () => {
    if (!oauthData?.oauth_url) return;

    stopPolling();
    const attemptToken = activeAttemptRef.current + 1;
    activeAttemptRef.current = attemptToken;
    attemptNumberRef.current += 1;
    const attemptNumber = attemptNumberRef.current;
    attemptStateRef.current = "pending";
    activeOAuthStateRef.current = oauthData.state;
    let attemptResumeMarked = false;
    const markAttemptStarted = (): void => {
      if (attemptResumeMarked) return;
      attemptResumeMarked = true;
      onAttemptStarted?.();
    };
    trackEvent(OnboardingEvents.SLACK_OAUTH_STARTED, { attempt: attemptNumber });
    setError(null);

    if (isMobileBrowser()) {
      markOAuthRedirectFlow(flow.providerName, oauthData.state);
      setPollingForCallback(true);
      const failRedirect = (): void => {
        clearRedirectWatch();
        clearOAuthRedirectFlow(flow.providerName, oauthData.state);
        if (
          attemptToken !== activeAttemptRef.current
          || attemptStateRef.current !== "pending"
        ) return;
        attemptStateRef.current = "settled";
        setPollingForCallback(false);
        trackEvent(OnboardingEvents.SLACK_CONNECTION_FAILED, { error: "navigation_no_op" });
        setError("Slack sign-in didn't open. Please try again.");
      };
      const onPageLifecycleChange = (event: Event): void => {
        const leaving = event.type === "pagehide"
          || (event.type === "visibilitychange" && document.visibilityState === "hidden");
        if (leaving) {
          markAttemptStarted();
          if (redirectTimerRef.current !== null) {
            clearTimeout(redirectTimerRef.current);
            redirectTimerRef.current = null;
          }
          return;
        }
        if (
          event.type === "pageshow"
          && !(event as PageTransitionEvent).persisted
        ) return;
        clearRedirectWatch();
        clearOAuthRedirectFlow(flow.providerName, oauthData.state);
        beginConnectionStatusGracePeriod(attemptToken, attemptNumber);
      };
      window.addEventListener("pagehide", onPageLifecycleChange);
      window.addEventListener("pageshow", onPageLifecycleChange);
      document.addEventListener("visibilitychange", onPageLifecycleChange);
      cancelRedirectListenersRef.current = () => {
        window.removeEventListener("pagehide", onPageLifecycleChange);
        window.removeEventListener("pageshow", onPageLifecycleChange);
        document.removeEventListener("visibilitychange", onPageLifecycleChange);
      };
      redirectTimerRef.current = window.setTimeout(
        failRedirect,
        REDIRECT_WATCHDOG_MS,
      );
      try {
        window.location.assign(oauthData.oauth_url);
      } catch {
        clearRedirectWatch();
        clearOAuthRedirectFlow(flow.providerName, oauthData.state);
        attemptStateRef.current = "settled";
        setPollingForCallback(false);
        trackEvent(OnboardingEvents.SLACK_CONNECTION_FAILED, { error: "redirect_failed" });
        setError("We couldn't open Slack sign-in. Please try again.");
      }
      return;
    }

    // Open OAuth URL in a popup window
    const popup = window.open(
      oauthData.oauth_url,
      "slack-oauth",
      "width=600,height=700,scrollbars=yes,resizable=yes"
    );

    if (!popup) {
      attemptStateRef.current = "settled";
      trackEvent(OnboardingEvents.SLACK_CONNECTION_FAILED, { error: "popup_blocked" });
      setError("Your browser blocked the Slack sign-in window. Allow pop-ups for this site, then try again.");
      return;
    }

    markAttemptStarted();
    setPollingForCallback(true);

    // Some providers isolate the popup's browsing context, which makes
    // popup.closed report true even while OAuth is still running. Treat closure
    // as a signal to start a backend grace-period poll, not as abandonment. The
    // reported failure was a 1920x1080 desktop replay, so BroadcastChannel is
    // still required even though mobile browsers use a full-page redirect.
    popupPollTimerRef.current = window.setInterval(() => {
      if (popup.closed) {
        beginConnectionStatusGracePeriod(attemptToken, attemptNumber);
      }
    }, 1000);

    popupTimeoutRef.current = window.setTimeout(() => {
      if (!popup.closed) {
        popup.close();
      }
      beginConnectionStatusGracePeriod(attemptToken, attemptNumber);
    }, 300000);
  };

  const beginConnectionStatusGracePeriod = (attemptToken: number, attemptNumber: number): void => {
    if (attemptToken !== activeAttemptRef.current || attemptStateRef.current !== "pending") return;
    clearPopupWatch();
    const abandonmentDeadline = Date.now() + POPUP_STATUS_GRACE_MS;
    const lateSuccessDeadline = Date.now() + POPUP_LATE_STATUS_WINDOW_MS;

    const checkStatus = async (): Promise<void> => {
      const attemptState = attemptStateRef.current;
      if (
        attemptToken !== activeAttemptRef.current
        || attemptState === "idle"
        || attemptState === "settled"
      ) return;

      try {
        const connected = await flow.isConnected();
        const latestAttemptState = attemptStateRef.current;
        if (
          attemptToken !== activeAttemptRef.current
          || latestAttemptState === "idle"
          || latestAttemptState === "settled"
        ) return;
        if (connected) {
          handleSuccess("status_poll");
          return;
        }
      } catch (err) {
        console.error("Failed to check Slack status:", err);
      }

      if (Date.now() >= abandonmentDeadline && attemptStateRef.current === "pending") {
        attemptStateRef.current = "abandoned";
        setPollingForCallback(false);
        trackEvent(OnboardingEvents.SLACK_OAUTH_ABANDONED, { attempt: attemptNumber });
        setError("Looks like the Slack sign-in wasn't finished. Ready when you are — just try again.");
      }

      if (Date.now() >= lateSuccessDeadline) return;

      const nextPollMs = attemptStateRef.current === "abandoned"
        ? POPUP_LATE_STATUS_POLL_MS
        : POPUP_STATUS_POLL_MS;
      statusPollTimerRef.current = window.setTimeout(checkStatus, nextPollMs);
    };

    void checkStatus();
  };

  const handleClose = () => {
    trackEvent(OnboardingEvents.SLACK_MODAL_CLOSED);
    activeAttemptRef.current += 1;
    attemptStateRef.current = "settled";
    if (activeOAuthStateRef.current) {
      clearOAuthRedirectFlow(flow.providerName, activeOAuthStateRef.current);
    }
    activeOAuthStateRef.current = null;
    stopPolling();
    setOauthData(null);
    setError(null);
    setPollingForCallback(false);
    onClose();
  };

  return (
    <PairingModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={flow.title}
    >
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">
            Setting up Slack connection...
          </p>
        </div>
      ) : pollingForCallback ? (
        <OAuthWaitingState iconBgClass="bg-green-100" iconColorClass="text-green-600" />
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {flow.description}
          </p>

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={openSlackOAuth}
              disabled={!oauthData?.oauth_url}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center space-x-2"
            >
              <span>{flow.buttonLabel}</span>
              <ArrowTopRightOnSquareIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </PairingModalShell>
  );
}
